import { IMatter, IMatterEvent } from "@/types/SPTypes.tsx";
import { translateEventType } from "@/utils/enums.ts";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";

function SelectedMatterOverview({
  matter,
  comments
}: {
  matter: IMatter;
  comments: IMatterEvent | any;
}) {
  const DESCRIPTION_LIMIT = 50;
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(false);
  }, [matter?.matterId]);

  const descriptionText = matter.matterDescription ?? "";
  const isLong = descriptionText.length > DESCRIPTION_LIMIT;
  const displayedDescription =
    isLong && !isExpanded
      ? `${descriptionText.slice(0, DESCRIPTION_LIMIT)}…`
      : descriptionText;

  const toggleExpanded = () => setIsExpanded(prev => !prev);

  const normalized = matter.matterResponse?.includes("\\n")
    ? matter.matterResponse.replace(/\\n/g, "\n")
    : matter.matterResponse;

  return (
    <Row>
      <Col xs={12} md={6}>
        <div
          className="bb-timeline"
          role="list"
          aria-label="Ärendehistorik"
          style={{ maxHeight: 520, overflowY: "auto" }}
        >
          <div className="bb-timeline-line" aria-hidden="true" />
          <article
            className="bb-timeline-item"
            role="listitem"
            aria-current={comments?.length === 0 ? "true" : undefined}
          >
            <div className="bb-timeline-dot" />

            <div className="bb-timeline-content">
              <div className="bb-timeline-header">
                <p className="bb-timeline-text">Ärende skapat</p>
                <span className="bb-timeline-date">
                  {(() => {
                    const dateStr = matter.createdDate ?? matter.Created;
                    if (!dateStr) return "Information saknas";
                    const dt = DateTime.fromISO(dateStr).setLocale("sv");
                    return dt.toFormat("d MMMM yyyy HH:mm");
                  })()}
                </span>
              </div>
            </div>
          </article>
          {comments?.map((matter: IMatterEvent | any, idx: number) => {
            new Date(matter.Created).toLocaleDateString("sv-SE", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            });
            const isLast = idx === comments.length - 1;
            return (
              <article
                key={matter.Id}
                className="bb-timeline-item"
                role="listitem"
                aria-current={isLast ? "true" : undefined}
              >
                <div className="bb-timeline-dot" />
                <div className="bb-timeline-content">
                  <div className="bb-timeline-header">
                    <p
                      className="bb-timeline-text"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {translateEventType(matter.eventType)}
                    </p>
                    <span className="bb-timeline-date">
                      {(() => {
                        const dateStr = matter.createdDate ?? matter.Created;
                        if (!dateStr) return "Information saknas";
                        const dt = DateTime.fromISO(dateStr).setLocale("sv");
                        return dt.toFormat("d MMMM yyyy HH:mm");
                      })()}
                    </span>
                  </div>
                  {matter.Title && (
                    <h4 className="bb-timeline-title">{matter.Title}</h4>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </Col>
      <Col xs={12} md={6}>
        <Card className="glass">
          <Card.Body>
            <Card.Title>Ärende-ID: {matter.ID}</Card.Title>
            <Card.Subtitle className="my-2 text-muted">
              <strong>Titel:</strong> {matter.matterTitle}
            </Card.Subtitle>
            <Card.Text className="text-muted mb-2">
              <strong>Ärendebeskrivning:</strong> {displayedDescription}
            </Card.Text>
            {isLong && (
              <Card.Link
                className="view-more"
                role="button"
                tabIndex={0}
                onClick={toggleExpanded}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleExpanded();
                  }
                }}
              >
                {isExpanded ? "Visa mindre" : "Visa mer"}
              </Card.Link>
            )}
            <Card.Text className="text-muted mb-2">
              <strong>Svar:</strong>{" "}
              {normalized ? normalized : "Ärendet inte inte besvarats ännu."}
            </Card.Text>
            {matter.clientMail && (
              <Card.Text className="text-muted mb-2">
                <strong>Du får återkoppling till följande E-post: </strong>
                {matter.clientMail}
              </Card.Text>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

export default SelectedMatterOverview;
